<template>
  <form @submit.prevent="registerAttempt">
    <div class="form-group" :class="{ error: $v.registerForm.username.$error }">
      <input
        :placeholder="$t('placeholder.username')"
        class="form-control"
        :disabled="loading || (finishingUserData && finishingUserData.username)"
        type="text"
        v-model.lazy="registerForm.username"
        @blur="$v.registerForm.username.$touch"
        autocomplete="username"
      />
      <template v-if="$v.registerForm.username.$error">
        <p class="error-message" v-if="!$v.registerForm.username.required">
          {{ $t("validations.required", { field: $t("fields.username") }) }}
        </p>
        <p class="error-message" v-if="!$v.registerForm.username.notInUse">
          {{ $t("validations.notInUse", { field: $t("fields.username") }) }}
        </p>
      </template>
    </div>
    <div class="form-group" :class="{ error: $v.registerForm.email.$error }">
      <input
        :placeholder="$t('placeholder.email')"
        class="form-control"
        :disabled="loading || (finishingUserData && finishingUserData.email)"
        type="email"
        v-model.lazy="registerForm.email"
        @blur="$v.registerForm.email.$touch"
        autocomplete="email"
      />
      <template v-if="$v.registerForm.email.$error">
        <p class="error-message" v-if="!$v.registerForm.email.required">
          {{ $t("validations.required", { field: $t("fields.email") }) }}
        </p>
        <p class="error-message" v-if="!$v.registerForm.email.email">
          {{ $t("validations.email", { field: $t("fields.email") }) }}
        </p>
        <p class="error-message" v-if="!$v.registerForm.email.notInUse">
          {{ $t("validations.notInUse", { field: $t("fields.email") }) }}
        </p>
      </template>
    </div>
    <div
      v-if="!this.finishingUserData"
      class="form-group"
      :class="{ error: $v.registerForm.email_confirm.$error }"
    >
      <input
        :placeholder="$t('placeholder.email_confirm')"
        class="form-control"
        :disabled="loading || (finishingUserData && finishingUserData.email)"
        type="email"
        v-model.lazy="registerForm.email_confirm"
        @blur="$v.registerForm.email_confirm.$touch"
      />
      <template v-if="$v.registerForm.email_confirm.$error">
        <p class="error-message" v-if="!$v.registerForm.email_confirm.required">
          {{
            $t("validations.required", { field: $t("fields.email_confirm") })
          }}
        </p>
        <p class="error-message" v-if="!$v.registerForm.email_confirm.sameAs">
          {{ $t("validations.sameAs", { field: $t("fields.email_confirm") }) }}
        </p>
      </template>
    </div>
    <div class="form-group" :class="{ error: $v.registerForm.password.$error }">
      <input
        class="form-control"
        :disabled="loading"
        type="password"
        v-model="registerForm.password"
        @input="$v.registerForm.password.$touch"
        :placeholder="$t('placeholder.password')"
        autocomplete="password"
      />
      <template v-if="$v.registerForm.password.$error">
        <p class="error-message" v-if="!$v.registerForm.password.required">
          {{ $t("validations.required", { field: $t("fields.password") }) }}
        </p>
      </template>
    </div>
    <div
      class="form-group"
      :class="{ error: $v.registerForm.password_confirm.$error }"
    >
      <input
        class="form-control"
        :disabled="loading"
        type="password"
        v-model="registerForm.password_confirm"
        @input="$v.registerForm.password_confirm.$touch"
        :placeholder="$t('placeholder.password_confirm')"
      />
      <template v-if="$v.registerForm.password_confirm.$error">
        <p
          class="error-message"
          v-if="!$v.registerForm.password_confirm.required"
        >
          {{
            $t("validations.required", { field: $t("fields.password_confirm") })
          }}
        </p>
        <p
          class="error-message"
          v-if="!$v.registerForm.password_confirm.sameAs"
        >
          {{
            $t("validations.sameAs", { field: $t("fields.password_confirm") })
          }}
        </p>
      </template>
    </div>
    <div
      class="captcha-container"
      :sitekey="recaptchaPublicKey"
      v-if="recaptchaPublicKey"
    >
      <vue-recaptcha
        ref="recaptcha"
        :sitekey="recaptchaPublicKey"
        :loadRecaptchaScript="true"
        :theme="'light'"
        @verify="registerForm.recaptcha = $event"
        @expire="registerForm.recaptcha = null"
      />
      <template v-if="$v.registerForm.recaptcha.$error">
        <p class="error-message" v-if="!$v.registerForm.recaptcha.required">
          {{ $t("validations.captcha") }}
        </p>
      </template>
    </div>

    <div class="messages">
      <div v-for="(errors, errorKey) in registerErrors" :key="errorKey">
        <p v-for="error in errors" :key="error">
          {{ error }}
        </p>
      </div>
    </div>

    <div
      class="form-group legals"
      :class="{ error: $v.registerForm.over_age.$error }"
    >
      <label>
        <input type="checkbox" v-model="registerForm.over_age" />
        <i18n
          path="subscription.payment_legals.accept.over_age"
          tag="span"
        ></i18n>
      </label>
      <template v-if="$v.registerForm.over_age.$error">
        <p class="error-message" v-if="!$v.registerForm.over_age.accepted">
          {{ $t("validations.accepted", { field: $t("fields.terms") }) }}
        </p>
      </template>
    </div>

    <div
      class="form-group legals"
      :class="{ error: $v.registerForm.use_of_images.$error }"
    >
      <label>
        <input type="checkbox" v-model="registerForm.use_of_images" />
        <i18n
          path="subscription.payment_legals.accept.use_of_images"
          tag="span"
        ></i18n>
      </label>
      <template v-if="$v.registerForm.use_of_images.$error">
        <p class="error-message" v-if="!$v.registerForm.use_of_images.accepted">
          {{ $t("validations.accepted", { field: $t("fields.terms") }) }}
        </p>
      </template>
    </div>

    <div
      class="form-group legals"
      :class="{ error: $v.registerForm.terms.$error }"
    >
      <label>
        <input type="checkbox" v-model="registerForm.terms" />
        <i18n path="subscription.payment_legals.accept.register" tag="span">
          <template>
            <a :href="termsUrl" target="_blank">{{
              $t("subscription.payment_legals.accept.terms")
            }}</a>
          </template>
          <template>
            <a :href="privacyUrl">{{
              $t("subscription.payment_legals.accept.privacy")
            }}</a>
          </template>
        </i18n>
      </label>
      <template v-if="$v.registerForm.terms.$error">
        <p class="error-message" v-if="!$v.registerForm.terms.accepted">
          {{ $t("validations.accepted", { field: $t("fields.terms") }) }}
        </p>
      </template>
    </div>

    <div class="buttons">
      <button class="btn-register" v-if="!loading" type="submit">
        {{ $t("register.buttons.register") }}
      </button>
      <Spinner v-else />
    </div>
  </form>
</template>

<script>
import Spinner from "./Spinner";
import { required, email, sameAs, requiredIf } from "vuelidate/lib/validators";
import resources from "../resources";
import VueRecaptcha from "vue-recaptcha";
import eventbus from "../eventbus";

export default {
  components: {
    Spinner,
    VueRecaptcha,
  },
  props: {
    finishingUserData: {
      required: false,
      default() {
        return null;
      },
    },
  },
  data() {
    return {
      registerForm: {
        email: "",
        email_confirm: "",
        username: "",
        password: "",
        password_confirm: "",
        recaptcha: null,
        over_age: false,
        use_of_images: false,
        finish_token: null,
      },
      loading: false,
      registerErrors: {},
    };
  },
  created() {},
  methods: {
    registerAttempt() {
      if (this.$v.registerForm.$invalid) {
        return this.$v.registerForm.$touch();
      }

      this.loading = true;

      this.$auth.register({
        body: this.registerForm,
        success: function (resp) {
          this.loading = false;
          eventbus.newRegister();
          this.$emit("successfullRegister", resp);
        },
        error: function (resp) {
          if (this.recaptchaPublicKey) {
            this.$refs.recaptcha.reset();
            this.registerForm.recaptcha = null;
            //TODO: Unset dirty flag
          }

          this.loading = false;
          this.registerErrors = resp.data.errors || {
            general: [this.$t("error")],
          };
          this.$emit("failedRegister", resp);
        },
        redirect: null,
        autoLogin: true,
      });
    },
  },
  computed: {
    recaptchaPublicKey() {
      return process.env.VUE_APP_RECAPTCHA_PUBLIC_KEY;
    },
    termsUrl() {
      let langCode = this.$i18n.locale;
      return `/legals/terms/${langCode}.html`;
    },
    privacyUrl() {
      return "https://divasplay.com/legals/privacy.html";
    },
  },
  validations() {
    return {
      registerForm: {
        email: {
          required,
          email,
          notInUse(value) {
            if (value === "" || this.finishingUserData) {
              return true;
            }

            return new Promise((resolve, reject) => {
              resources.unavailableEmails
                .index({ email: value })
                .then((resp) => {
                  resolve(resp.data.data.length === 0);
                });
            });
          },
        },
        email_confirm: {
          required: requiredIf(() => !this.finishingUserData),
          // email,
          sameAs(value) {
            return this.finishingUserData || value === this.registerForm.email;
          },
        },
        username: {
          required,
          notInUse(value) {
            if (value === "" || this.finishingUserData) return true;

            return new Promise((resolve, reject) => {
              resources.unavailableUsernames
                .index({ username: value })
                .then((resp) => {
                  console.log(resp.data.data.length);
                  resolve(resp.data.data.length === 0);
                })
                .catch((err) => resolve(false));
            });
          },
        },
        password: {
          required,
        },
        password_confirm: {
          required,
          sameAsPassword: sameAs("password"),
        },
        recaptcha: {
          required: requiredIf(() => this.recaptchaPublicKey),
        },
        terms: {
          accepted(value) {
            return value === true;
          },
        },
        over_age: {
          accepted(value) {
            return value === true;
          },
        },
        use_of_images: {
          accepted(value) {
            return value === true;
          },
        },
      },
    };
  },
  watch: {
    loading() {
      this.$emit("loadingChange", this.loading);
    },
    finishingUserData: {
      handler(finishingUserData) {
        if (finishingUserData) {
          Object.keys(finishingUserData).forEach((key) => {
            if (finishingUserData.hasOwnProperty(key)) {
              this.$set(this.registerForm, key, finishingUserData[key]);
            }
          });
        }
      },
      immediate: true,
    },
  },
};
</script>

<style scoped lang="scss">
@import "../styles/classes";
@import "../styles/variables";

.buttons {
  margin-bottom: 30px;
}

.btn-register {
  @extend %fresh-btn;
  display: block;
  width: 100%;
  padding: 1em;
}

.legals {
  a {
    font-weight: bold;
    text-decoration: underline;
  }

  span {
    font-size: 0.75em;
  }
}
</style>
